import {Suspense} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApi} from '@di-shared/api/di';
import AnalyticPageView from '@di-shared/features/analytics/AnalyticPageView';
import {DEMERIT_EDUCATION_GA_SCREEN} from '@di-shared/features/public-service/demerit-education/demeritEducationGaScreenNames';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import Header from '@di-shared/features/common/Header';
import HeaderBackButton from '@di-shared/features/common/HeaderBackButton';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import {DemeritEducationReservationEditFormType} from '@di-shared/features/public-service/demerit-education/DemeritEducationReservationEditForm';
import {getQueryKey} from '@shared/queryKeyMap';
import usePendingHandler from '@shared/usePendingHandler';
import {useSuspenseQuery} from '@tanstack/react-query';

interface DemeritEducationReservationEditStep2BranchProps {
  form: DemeritEducationReservationEditFormType;
  onUpdateForm(partial: Partial<DemeritEducationReservationEditFormType>): void;
  onNext(): void;
}

function DemeritEducationReservationEditStep2Branch(props: DemeritEducationReservationEditStep2BranchProps) {
  const {t} = useInferableTranslation();
  const {backOrClose, openAlert} = useCcWebviewUtilsHelper();
  const [handleError] = usePendingHandler(async () => {
    await openAlert({
      title: t('T10314'),
      message: t('T10328'),
      okText: t('T10221'),
    });
    backOrClose();
  });

  return (
    <ErrorBoundary
      fallbackRender={() => null}
      onError={handleError}
    >
      <Suspense
        fallback={
          <div className="wrap">
            <Header left={<HeaderBackButton />}>
              <h1 className="title_xsmall_b nav_center_title">{t('T10317')}</h1>
            </Header>
            <main className="content">
              <section className="section">
                <div className="section_container_header">
                  <h2 className="title_small_b">{t('T10256')}</h2>
                </div>
                <div className="place_select_area">
                  <div className="select_area_radio">
                    {['서울', '경기', '인천', '부산', '대구', '강원', '충북', '충남', '전북', '전남', '경북', '경남', '제주'].map(
                      (content) => (
                        <div
                          key={content}
                          className="select_box select_box_radio skeleton"
                        >
                          <input
                            type="radio"
                            className="input"
                            name="branch"
                          />
                          <label className="label">
                            <span className="text">{content}</span>
                          </label>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </section>
            </main>
            <div className="floating_action_area">
              <div className="button_box_complex">
                <button
                  type="button"
                  className="btn btn_large btn_outline btn_full"
                  onClick={backOrClose}
                >
                  {t('T10316')}
                </button>
                <button
                  type="button"
                  className="btn btn_large btn_filled btn_full"
                  disabled
                >
                  {t('T20348')}
                </button>
              </div>
            </div>
          </div>
        }
      >
        <Inner {...props} />
      </Suspense>
    </ErrorBoundary>
  );
}

function Inner({form, onUpdateForm, onNext}: DemeritEducationReservationEditStep2BranchProps) {
  const {t} = useInferableTranslation();
  const {backOrClose} = useCcWebviewUtilsHelper();


  const {data: branchData} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.demeritEducationController.getBranch)],
    queryFn: async () => {
      // 정상 조회 완료(B00) 이외에는 에러로 취급
      // B00 정상 | C95 연계 오류 | C96 암복호화에러 | C97 파라메터 없음 | C98 필수 파라메터 값 없음 | C99 시스템 오류
      // https://hmg.dooray.com/project/tasks/4117473847451959918
      const response = await diApi.demeritEducationController.getBranch();
      if (response.data.resultCode !== 'B00') {
        throw new ExpectedError('branch data not available');
      }
      return response;
    },
    select: ({data}) => data,
  });

  return (
    <div className="wrap">
      <AnalyticPageView screenName={DEMERIT_EDUCATION_GA_SCREEN.RESERVATION_EDIT__STEP_2_BRANCH} />
      <Header left={<HeaderBackButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('T10317')}</h1>
      </Header>
      <main className="content">
        <section className="section">
          <div className="section_container_header">
            <h2 className="title_small_b">{t('T10256')}</h2>
          </div>
          <div className="place_select_area">
            <div className="select_area_radio">
              {branchData.contents.map((content) => (
                <div
                  key={content.code}
                  className="select_box select_box_radio"
                >
                  <input
                    type="radio"
                    id={`branch_${content.code}`}
                    className="input"
                    name="branch"
                    checked={content.code === form.branchCode}
                    onChange={() =>
                      onUpdateForm({
                        branchName: content.name,
                        branchCode: content.code,
                        educationStartDate: null,
                        educationEndDate: null,
                        centerName: '',
                        centerCode: '',
                        centerNotice: '',
                        classroomCode: '',
                        educationDate: null,
                        educationStartTime: null,
                        educationEndTime: null,
                      })
                    }
                  />
                  <label
                    htmlFor={`branch_${content.code}`}
                    className="label"
                  >
                    <span className="text">{content.name}</span>
                  </label>
                </div>
              ))}
            </div>
          </div>
        </section>
      </main>
      <div className="floating_action_area">
        <div className="button_box_complex">
          <button
            type="button"
            className="btn btn_large btn_outline btn_full"
            onClick={backOrClose}
          >
            {t('T10316')}
          </button>
          <button
            type="button"
            className="btn btn_large btn_filled btn_full"
            disabled={!(form.branchName && form.branchCode)}
            onClick={onNext}
          >
            {t('T20348')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DemeritEducationReservationEditStep2Branch;
